import {
  Box,
  Flex,
  Text,
  Heading,
  VStack,
  Image,
  Icon,
  Container,
  SimpleGrid,
  Badge,
  Spinner,
  useBreakpointValue,
  Button,
  HStack,
  Tooltip,
  Divider,
  Grid,
  GridItem,
  Center,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useUser } from '../context/UserContext';
import { useNavigation } from '../context/NavigationContext';
import NeuralNetworkBackground from './NeuralNetworkBackground';
import { 
  Brain, 
  Heart, 
  Users, 
  LineChart, 
  Database, 
  Lightbulb, 
  Shield, 
  Network, 
  BarChart3, 
  Smartphone, 
  Gauge,
  Battery,
  Layers,
  Workflow,
  Clock,
  TrendingUp,
  Compass,
  Bot,
  DollarSign,
  Calendar,
  Target,
  Zap,
  Award,
  Briefcase,
  FileText,
  CheckCircle,
  Settings,
  PieChart,
  Activity,
  ThumbsUp,
  Clipboard,
  UserPlus,
  Sliders,
  BarChart,
  Cpu,
  GitMerge,
  Rocket,
  Bookmark,
  HelpCircle,
  ChevronRight,
  Play,
  Pause,
  Volume2,
  VolumeX
} from 'lucide-react';
import { useEffect, useState, useCallback } from 'react';
import SlideContainer from './SlideContainer';
import { useAudio } from '../context/AudioContext';
import SlideControls from './SlideControls';
import BrandingBanner from './BrandingBanner';

// Methodist color palette
const methodistBlue = '#0066CC';
const methodistNavy = '#002D72';
const methodistGreen = '#38A169';
const methodistPurple = '#805AD5';

// Feature colors
const aiLavender = methodistPurple;
const techBlue = methodistBlue;
const healthGreen = methodistGreen;
const businessNavy = methodistNavy;
const wellnessOrange = '#DD6B20';

interface WelcomeStartScreenProps {
  onStart?: () => void;
  mode?: 'standalone' | 'deck';
  onNavigateToSlide?: (slideId: string) => void;
  isSpeaking?: boolean;
  showCaptions?: boolean;
  captionsText?: string;
  onNext?: () => void;
  onPrevious?: () => void;
  onOpenJIMIChat?: () => void;
  onHelp?: () => void;
  details?: {
    uiSample: 'resilience' | 'culture' | 'efficiency' | 'ai-companion' | 'crowdsourcing' | 'governance';
  };
}

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);
const MotionButton = motion(Button);

const WelcomeStartScreen = ({ 
  onStart, 
  mode = 'standalone',
  onNavigateToSlide,
  isSpeaking,
  showCaptions,
  captionsText,
  onNext,
  onPrevious,
  onOpenJIMIChat,
  onHelp,
  details,
}: WelcomeStartScreenProps) => {
  const { user } = useUser();
  const navigation = useNavigation();
  const [isStarted, setIsStarted] = useState(false);
  const [isLogoLoaded, setIsLogoLoaded] = useState(false);
  
  // Get audio context
  const { 
    playAudioForSlide, 
    stopAllAudio, 
    isPlaying,
    isMuted,
    togglePlayback,
    toggleMute 
  } = useAudio();

  // Add state for active tab
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const totalTabs = 5; // Total number of tabs
  
  // Play welcome audio on mount
  useEffect(() => {
    console.log('[WelcomeStartScreen] Component mounted');
    
    // Stop any existing audio first
    stopAllAudio();
    
    // Play welcome audio after a short delay to ensure clean start
    const timer = setTimeout(() => {
      console.log('[WelcomeStartScreen] Playing welcome audio');
      playAudioForSlide('welcome-start');
    }, 100);

    return () => {
      console.log('[WelcomeStartScreen] Component unmounting');
      clearTimeout(timer);
      stopAllAudio();
    };
  }, [playAudioForSlide, stopAllAudio]);

  const handleStart = useCallback(() => {
    console.log('[WelcomeStartScreen] Start button clicked, mode:', mode);
    stopAllAudio();
    setIsStarted(true);

    if (mode === 'standalone') {
      console.log('[WelcomeStartScreen] Standalone mode - navigating to presenter-bio');
      if (onNavigateToSlide) {
        onNavigateToSlide('presenter-bio');
      } else if (onStart) {
        onStart();
      }
    } else {
      console.log('[WelcomeStartScreen] Deck mode - calling onStart');
      if (onStart) {
        // Stop audio and wait for it to clean up before transitioning
        setTimeout(() => {
          onStart();
        }, 100);
      } else if (navigation) {
        navigation.nextSlide();
      }
    }
  }, [mode, onStart, onNavigateToSlide, navigation, stopAllAudio]);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowRight') {
      console.log('[WelcomeStartScreen] Navigation key pressed');
      handleStart();
    }
  }, [handleStart]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Define all features - reduced to 3 per category
  const allFeatures = [
    // Stanford Model - 3 items
    { title: 'Professional Fulfillment', icon: Heart, category: 'Stanford Model', description: 'Enhancing clinician satisfaction and meaning in work' },
    { title: 'Culture of Wellness', icon: Users, category: 'Stanford Model', description: 'Fostering a supportive healthcare environment' },
    { title: 'Practice Efficiency', icon: Gauge, category: 'Stanford Model', description: 'Optimizing workflows and reducing burnout factors' },
    
    // Platform Features - 3 items
    { title: 'AI Companion', icon: Bot, category: 'Platform', description: 'Personalized AI support for clinicians' },
    { title: 'Health Vitals', icon: Activity, category: 'Platform', description: 'Real-time monitoring of wellness metrics' },
    { title: 'Smart Guidance', icon: Compass, category: 'Platform', description: 'Adaptive recommendations based on data' },
    
    // Development and Implementation - 3 items
    { title: 'Agile Framework', icon: GitMerge, category: 'Development', description: 'Flexible, iterative development approach' },
    { title: 'User-Centered', icon: Target, category: 'Development', description: 'Designed around clinician needs' },
    { title: 'Continuous Integration', icon: Layers, category: 'Development', description: 'Seamless updates and enhancements' },
    
    // Business Case - 3 items
    { title: 'ROI Analysis', icon: TrendingUp, category: 'Business', description: 'Quantifiable benefits and returns' },
    { title: 'Cost Reduction', icon: DollarSign, category: 'Business', description: 'Decreased burnout-related expenses' },
    { title: 'Productivity Gains', icon: BarChart, category: 'Business', description: 'Improved efficiency and output' },
    
    // Next Steps - 3 items
    { title: 'Implementation Plan', icon: Clipboard, category: 'Next Steps', description: 'Structured rollout strategy' },
    { title: 'Pilot Program', icon: Rocket, category: 'Next Steps', description: 'Initial deployment and testing' },
    { title: 'Feedback Loop', icon: Sliders, category: 'Next Steps', description: 'Continuous improvement process' },
  ];

  // Responsive adjustments
  const headingSize = useBreakpointValue({ base: 'xl', md: '2xl' });
  const subheadingSize = useBreakpointValue({ base: 'md', md: 'lg' });
  const isLargerThanMd = useBreakpointValue({ base: false, md: true });

  // Group features by category for more compact display
  const stanfordFeatures = allFeatures.filter(feature => feature.category === 'Stanford Model');
  const platformFeatures = allFeatures.filter(feature => feature.category === 'Platform');
  const developmentFeatures = allFeatures.filter(feature => feature.category === 'Development');
  const businessFeatures = allFeatures.filter(feature => feature.category === 'Business');
  const nextStepsFeatures = allFeatures.filter(feature => feature.category === 'Next Steps');

  // Render the slide controls
  const renderSlideControls = useCallback(() => {
    return (
      <SlideControls
        currentSlide={0}
        totalSlides={1}
        onNext={handleStart}
        onPrevious={() => {}}
        onSlideSelect={() => {}}
        isSpeaking={isPlaying}
        onToggleSpeech={togglePlayback}
        showCaptions={false}
        onToggleCaptions={() => {}}
        isWelcomeScreen={true}
        isMuted={isMuted}
        toggleMute={toggleMute}
        slides={[{ id: 'welcome-start', title: 'Welcome' }]}
      />
    );
  }, [handleStart, isPlaying, isMuted, togglePlayback, toggleMute]);

  return (
    <Box position="relative" minH="100vh" overflow="hidden" bg="white">
      <NeuralNetworkBackground opacity={0.05} />
      
      <SlideContainer>
        <Container maxW="container.xl" h="100vh" position="relative">
          <Flex
            direction="column"
            h="full"
            justify="space-between"
            align="center"
            py={{ base: 4, md: 6 }}
            px={{ base: 2, md: 4 }}
          >
            {/* Main content */}
            <VStack spacing={4} align="center" width="100%" flex="1">
              {/* Header with Logo and Title - Centered */}
              <MotionBox
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                textAlign="center"
                w="full"
                mb={6}
              >
                {/* Branding Section - Centered */}
                <MotionFlex 
                  width="100%" 
                  direction="column"
                  justify="center" 
                  align="center" 
                  mb={{ base: 3, sm: 4 }}
                  gap={4}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 } as any}
                >
                  {/* Branding Banner */}
                  <BrandingBanner 
                    showTagline={true}
                    jimiLogoSize="lg"
                    methodistLogoHeight="60px"
                    mb={8}
                  />
                </MotionFlex>
              </MotionBox>

              {/* Main Content Grid - More compact layout with better alignment */}
              <MotionBox
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.5 }}
                w="full"
                px={{ base: 2, md: 4 }}
                flex="1"
                overflowY="auto"
                css={{
                  '&::-webkit-scrollbar': {
                    width: '4px',
                  },
                  '&::-webkit-scrollbar-track': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'gray.200',
                    borderRadius: '24px',
                  },
                }}
              >
                <Grid
                  templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)' }}
                  gap={6}
                  mb={6}
                  alignItems="stretch"
                >
                  {/* Left Column */}
                  <GridItem colSpan={1} display="flex" flexDirection="column">
                    {/* Stanford Model */}
                    <Box 
                      bg="white" 
                      borderRadius="md" 
                      boxShadow="sm"
                      p={4} 
                      mb={6}
                      borderTop="4px solid"
                      borderColor={methodistPurple}
                      height="100%"
                      display="flex"
                      flexDirection="column"
                    >
                      <Flex align="center" mb={3} justify="center">
                        <Box
                          bg={methodistPurple}
                          color="white"
                          borderRadius="full"
                          w="32px"
                          h="32px"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          fontWeight="bold"
                          fontSize="md"
                          mr={3}
                        >
                          1
                        </Box>
                        <Heading size="md" color={methodistPurple}>
                          Stanford Model
                        </Heading>
                      </Flex>
                      
                      <Text fontSize="sm" color="gray.600" mb={4} textAlign="center">
                        A comprehensive framework addressing clinician wellness through three key dimensions
                      </Text>
                      
                      <SimpleGrid columns={1} spacing={3} flex="1">
                        {stanfordFeatures.map((feature) => (
                          <Flex
                            key={feature.title}
                            bg="white"
                            p={3}
                            borderRadius="md"
                            boxShadow="xs"
                            border="1px solid"
                            borderColor="gray.100"
                            align="center"
                            transition="all 0.2s"
                            _hover={{ transform: 'translateY(-2px)', boxShadow: 'sm' }}
                          >
                            <Box
                              bg={`${methodistPurple}15`}
                              p={2}
                              borderRadius="md"
                              mr={3}
                            >
                              <Icon
                                as={feature.icon}
                                color={methodistPurple}
                                boxSize={5}
                              />
                            </Box>
                            <Box>
                              <Text
                                fontWeight="semibold"
                                color="gray.700"
                                fontSize="sm"
                                mb={0.5}
                              >
                                {feature.title}
                              </Text>
                              <Text fontSize="xs" color="gray.600">
                                {feature.description}
                              </Text>
                            </Box>
                          </Flex>
                        ))}
                      </SimpleGrid>
                    </Box>
                    
                    {/* Platform Features */}
                    <Box 
                      bg="white" 
                      borderRadius="md" 
                      boxShadow="sm"
                      p={4} 
                      mb={6}
                      borderTop="4px solid"
                      borderColor={methodistGreen}
                      height="100%"
                      display="flex"
                      flexDirection="column"
                    >
                      <Flex align="center" mb={3} justify="center">
                        <Box
                          bg={methodistGreen}
                          color="white"
                          borderRadius="full"
                          w="32px"
                          h="32px"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          fontWeight="bold"
                          fontSize="md"
                          mr={3}
                        >
                          2
                        </Box>
                        <Heading size="md" color={methodistGreen}>
                          Platform Features
                        </Heading>
                      </Flex>
                      
                      <Text fontSize="sm" color="gray.600" mb={4} textAlign="center">
                        Innovative AI-driven tools designed to support clinician wellness and performance
                      </Text>
                      
                      <SimpleGrid columns={1} spacing={3} flex="1">
                        {platformFeatures.map((feature) => (
                          <Flex
                            key={feature.title}
                            bg="white"
                            p={3}
                            borderRadius="md"
                            boxShadow="xs"
                            border="1px solid"
                            borderColor="gray.100"
                            align="center"
                            transition="all 0.2s"
                            _hover={{ transform: 'translateY(-2px)', boxShadow: 'sm' }}
                          >
                            <Box
                              bg={`${methodistGreen}15`}
                              p={2}
                              borderRadius="md"
                              mr={3}
                            >
                              <Icon
                                as={feature.icon}
                                color={methodistGreen}
                                boxSize={5}
                              />
                            </Box>
                            <Box>
                              <Text
                                fontWeight="semibold"
                                color="gray.700"
                                fontSize="sm"
                                mb={0.5}
                              >
                                {feature.title}
                              </Text>
                              <Text fontSize="xs" color="gray.600">
                                {feature.description}
                              </Text>
                            </Box>
                          </Flex>
                        ))}
                      </SimpleGrid>
                    </Box>
                  </GridItem>
                  
                  {/* Right Column */}
                  <GridItem colSpan={1} display="flex" flexDirection="column">
                    {/* Development */}
                    <Box 
                      bg="white" 
                      borderRadius="md" 
                      boxShadow="sm"
                      p={4} 
                      mb={6}
                      borderTop="4px solid"
                      borderColor={methodistBlue}
                      height="100%"
                      display="flex"
                      flexDirection="column"
                    >
                      <Flex align="center" mb={3} justify="center">
                        <Box
                          bg={methodistBlue}
                          color="white"
                          borderRadius="full"
                          w="32px"
                          h="32px"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          fontWeight="bold"
                          fontSize="md"
                          mr={3}
                        >
                          3
                        </Box>
                        <Heading size="md" color={methodistBlue}>
                          Development Approach
                        </Heading>
                      </Flex>
                      
                      <Text fontSize="sm" color="gray.600" mb={4} textAlign="center">
                        User-centered methodology ensuring seamless integration into clinical workflows
                      </Text>
                      
                      <SimpleGrid columns={1} spacing={3} flex="1">
                        {developmentFeatures.map((feature) => (
                          <Flex
                            key={feature.title}
                            bg="white"
                            p={3}
                            borderRadius="md"
                            boxShadow="xs"
                            border="1px solid"
                            borderColor="gray.100"
                            align="center"
                            transition="all 0.2s"
                            _hover={{ transform: 'translateY(-2px)', boxShadow: 'sm' }}
                          >
                            <Box
                              bg={`${methodistBlue}15`}
                              p={2}
                              borderRadius="md"
                              mr={3}
                            >
                              <Icon
                                as={feature.icon}
                                color={methodistBlue}
                                boxSize={5}
                              />
                            </Box>
                            <Box>
                              <Text
                                fontWeight="semibold"
                                color="gray.700"
                                fontSize="sm"
                                mb={0.5}
                              >
                                {feature.title}
                              </Text>
                              <Text fontSize="xs" color="gray.600">
                                {feature.description}
                              </Text>
                            </Box>
                          </Flex>
                        ))}
                      </SimpleGrid>
                    </Box>
                    
                    {/* Business Case */}
                    <Box 
                      bg="white" 
                      borderRadius="md" 
                      boxShadow="sm"
                      p={4} 
                      mb={6}
                      borderTop="4px solid"
                      borderColor={methodistNavy}
                      height="100%"
                      display="flex"
                      flexDirection="column"
                    >
                      <Flex align="center" mb={3} justify="center">
                        <Box
                          bg={methodistNavy}
                          color="white"
                          borderRadius="full"
                          w="32px"
                          h="32px"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          fontWeight="bold"
                          fontSize="md"
                          mr={3}
                        >
                          4
                        </Box>
                        <Heading size="md" color={methodistNavy}>
                          Business Value
                        </Heading>
                      </Flex>
                      
                      <Text fontSize="sm" color="gray.600" mb={4} textAlign="center">
                        Quantifiable benefits demonstrating return on investment and organizational impact
                      </Text>
                      
                      <SimpleGrid columns={1} spacing={3} flex="1">
                        {businessFeatures.map((feature) => (
                          <Flex
                            key={feature.title}
                            bg="white"
                            p={3}
                            borderRadius="md"
                            boxShadow="xs"
                            border="1px solid"
                            borderColor="gray.100"
                            align="center"
                            transition="all 0.2s"
                            _hover={{ transform: 'translateY(-2px)', boxShadow: 'sm' }}
                          >
                            <Box
                              bg={`${methodistNavy}15`}
                              p={2}
                              borderRadius="md"
                              mr={3}
                            >
                              <Icon
                                as={feature.icon}
                                color={methodistNavy}
                                boxSize={5}
                              />
                            </Box>
                            <Box>
                              <Text
                                fontWeight="semibold"
                                color="gray.700"
                                fontSize="sm"
                                mb={0.5}
                              >
                                {feature.title}
                              </Text>
                              <Text fontSize="xs" color="gray.600">
                                {feature.description}
                              </Text>
                            </Box>
                          </Flex>
                        ))}
                      </SimpleGrid>
                    </Box>
                  </GridItem>
                </Grid>
                
                {/* Next Steps - Bottom section */}
                <Box 
                  bg="white" 
                  borderRadius="md" 
                  boxShadow="sm"
                  p={4} 
                  mb={4}
                  borderTop="4px solid"
                  borderColor={wellnessOrange}
                >
                  <Flex align="center" mb={3} justify="center">
                    <Box
                      bg={wellnessOrange}
                      color="white"
                      borderRadius="full"
                      w="32px"
                      h="32px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      fontWeight="bold"
                      fontSize="md"
                      mr={3}
                    >
                      5
                    </Box>
                    <Heading size="md" color={wellnessOrange}>
                      Next Steps
                    </Heading>
                  </Flex>
                  
                  <Text fontSize="sm" color="gray.600" mb={4} textAlign="center">
                    Strategic roadmap for successful implementation and adoption across Methodist Health System
                  </Text>
                  
                  <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={4} justifyItems="center">
                    {nextStepsFeatures.map((feature) => (
                      <MotionBox
                        key={feature.title}
                        bg="white"
                        p={3}
                        borderRadius="md"
                        boxShadow="xs"
                        border="1px solid"
                        borderColor="gray.100"
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        textAlign="center"
                        initial={{ opacity: 0.8, y: 5 }}
                        whileHover={{ 
                          scale: 1.05, 
                          boxShadow: "0 8px 12px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
                          opacity: 1,
                          y: 0
                        }}
                        whileTap={{ scale: 0.98 }}
                        width="100%"
                        maxW="250px"
                      >
                        <Box
                          bg={`${wellnessOrange}15`}
                          p={2}
                          borderRadius="md"
                          mb={2}
                          transition="all 0.3s"
                          _groupHover={{ bg: `${wellnessOrange}25` }}
                        >
                          <Icon
                            as={feature.icon}
                            color={wellnessOrange}
                            boxSize={5}
                          />
                        </Box>
                        <Text
                          fontWeight="semibold"
                          color="gray.700"
                          fontSize="sm"
                          mb={1}
                        >
                          {feature.title}
                        </Text>
                        <Text fontSize="xs" color="gray.600" mb={2}>
                          {feature.description}
                        </Text>
                        <Badge 
                          colorScheme="orange" 
                          variant="subtle" 
                          fontSize="xs"
                          borderRadius="full"
                          px={2}
                        >
                          Ready
                        </Badge>
                      </MotionBox>
                    ))}
                  </SimpleGrid>
                </Box>
                
                {/* Call to Action Button */}
                <Flex justify="center" mb={2}>
                  <MotionButton
                    size="lg"
                    colorScheme="blue"
                    bg={methodistBlue}
                    color="white"
                    px={8}
                    py={5}
                    fontSize="md"
                    fontWeight="bold"
                    borderRadius="md"
                    onClick={handleStart}
                    rightIcon={<ChevronRight size={20} />}
                    _hover={{ bg: "blue.600", transform: "translateY(-2px)" }}
                    _active={{ bg: "blue.700" }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8, duration: 0.5 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Continue to Presentation
                  </MotionButton>
                </Flex>
              </MotionBox>
            </VStack>
          </Flex>
        </Container>
      </SlideContainer>
      
      {/* Slide Controls */}
      {renderSlideControls()}
    </Box>
  );
};

export default WelcomeStartScreen;
